import dotProp from "dot-prop";
import Fuse from "fuse.js";

import { SearchKeyOptions } from "./interfaces";

/**
 * Iterates over every item and determines the items to be fuzzy searched based on a single property or with multiple.
 *
 * @param items - An array of items.
 * @param property - Property used to match the term either a string or an array of strings that serve as fallbacks.
 */
const findItemsMatchingProperty = (
	items: any[],
	property: string | string[],
	searchKeyOptions: SearchKeyOptions = {},
) => {
	const searchItems = [];

	for (const originalItem of items) {
		// When property is a string check if it points to a string in the object
		if (typeof property === "string") {
			const item = dotProp.get(originalItem, property);

			// When item points to a string the item is added to searchItems
			if (typeof item === "string") {
				searchItems.push({
					item,
					originalItem,
				});
			}
		} else if (Array.isArray(property) && property.length > 0) {
			// When property is an array check for each  element if it points to a string in the object
			for (const fallback of property) {
				const item = dotProp.get(originalItem, fallback);

				// When the fallback doesn't point to a string skip to the next item
				if (typeof item !== "string") {
					continue;
				}

				// When the fallback points to a string the item is added to searchItems
				searchItems.push({
					item,
					originalItem,
				});

				// If we don't want to match all the options, once a match for a fallback is found, there is no use in further iterating the fallbacks
				if (searchKeyOptions.searchKeyType === "fallback") {
					break;
				}
			}
		}
	}

	return searchItems;
};

/**
 * Fuzzy search method to filter the items of a ic-search component.
 *
 * @param items - An array of items.
 * @param searchTerm - Term used to match.
 * @param property - Property used to match the term.
 */
export const fuzzy = (
	items: any[],
	searchTerm: string,
	property?: string | string[],
	searchKeyOptions?: SearchKeyOptions,
) => {
	const searchProperty = "item";
	// eslint-disable-next-line no-useless-assignment
	let searchItems = [];

	if (!searchTerm || searchTerm.trim() === "") {
		// Bail out early
		return items;
	}

	// If no property was passed, we're searching on the item itself ( in case of an array of strings, numbers, etc.)
	if (!property) {
		searchItems = items.map((item) => {
			return { item };
		});
	} else {
		// Search for items that have the exact property, if not try fallback(s)
		searchItems = findItemsMatchingProperty(items, property, searchKeyOptions);
	}

	const fuse = new Fuse(searchItems, {
		keys: [searchProperty],
		threshold: 0.3,
	});

	const results = fuse.search(searchTerm).map((result) => result.item);

	if (!property) {
		return results.map((x) => x.item);
	}

	// Return the original items
	let mappedResults = results.map((x) => x["originalItem"]);

	if (searchKeyOptions && searchKeyOptions.trackBy) {
		const uniqueValues = new Map<string, any>(
			mappedResults.map((item): [string, any] => [item[searchKeyOptions.trackBy], item]),
		).values();

		mappedResults = Array.from(uniqueValues);
	}

	// Filter for the unique objects
	return Array.from(new Set(mappedResults));
};
